import { useCallback, useState } from "react";

import { useFormik } from "formik";
import i18next from "i18next";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { activateByToken } from "../../../protocols";
import palette from "../../../theme/palette";

import { useEventListener, useLocalStorage } from "../../../hooks";

import { useAuth } from "../../../context/auth";

import { DotsLoadingComponent, InputComponent } from "../../ui";

import { Link } from "react-router-dom";
import {
  BackButton,
  BackButtonBlock,
  BackButtonLabel,
  ButtonBlock,
  CardWrapper,
  FormSubtitle,
  FormTitle,
  SubmitButton,
  SubmitButtonLoadingCover,
  Wrapper,
} from "../RegistrationForm/style";

const ActivateByTokenForm = ({ token }) => {
  const { t } = i18next;
  const { setItem } = useLocalStorage();
  const { getUserData, onChangeIsAuth } = useAuth();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationSchema = Yup.object().shape({
    password: Yup.string().min(6, t("must_field_characters")).required(t("required")),
    confirmationPassword: Yup.string()
      .min(6, t("must_field_characters"))
      .required(t("required"))
      .oneOf([Yup.ref("password"), null], t("passwords_must_match")),
  });

  const handleFormSubmit = async values => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const activationResult = await activateByToken({
        token: token,
        password: values.password,
      });

      setItem("token", activationResult?.data?.token);

      await getUserData(activationResult?.data);
      onChangeIsAuth(true);

      setIsSubmitting(false);
      navigate("/");
    } catch (error) {
      setFieldError("password", error?.response?.data?.message || t("error_activating_user"));
      setIsSubmitting(false);
    }
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldError } = useFormik({
    initialValues: {
      password: "",
      confirmationPassword: "",
    },
    validationSchema,
    onSubmit: handleFormSubmit,
    validateOnBlur: true,
  });

  const handleKeyPress = useCallback(
    e => {
      if (e.key === "Enter" || e.keyCode === 13) {
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  useEventListener("keypress", handleKeyPress);

  return (
    <Wrapper>
      <CardWrapper>
        <FormTitle>{t("activation_title")}</FormTitle>

        <FormSubtitle>{t("register_to_save_data")}</FormSubtitle>

        <InputComponent
          value={values.password}
          onChange={handleChange("password")}
          type="password"
          placeholder={t("enter_activation_password")}
          label={t("password_text")}
          error={errors.password}
          onBlur={handleBlur("password")}
          touched={touched.password}
          required
        />

        <InputComponent
          value={values.confirmationPassword}
          onChange={handleChange("confirmationPassword")}
          type="password"
          placeholder={t("confirmation_activation_password")}
          label={t("password_confirm")}
          error={errors.confirmationPassword}
          onBlur={handleBlur("confirmationPassword")}
          touched={touched.confirmationPassword}
          required
        />

        <ButtonBlock>
          <SubmitButton onClick={handleSubmit} type="button">
            {t("activate")}
            {isSubmitting && (
              <SubmitButtonLoadingCover>
                <DotsLoadingComponent color={palette.white} />
              </SubmitButtonLoadingCover>
            )}
          </SubmitButton>
        </ButtonBlock>

        <BackButtonBlock>
          <BackButtonLabel>{t("logged_user_text")}</BackButtonLabel>
          <BackButton>
            <Link to="/auth">{t("login_text")}</Link>
          </BackButton>
        </BackButtonBlock>
      </CardWrapper>
    </Wrapper>
  );
};

export default ActivateByTokenForm;
